import { Directive, HostListener, OnDestroy } from '@angular/core';
import dayjs from 'dayjs';
import { Subject, Subscription, take, throwError } from 'rxjs';
import Swal from 'sweetalert2';
import { CartBillService, CustomerService, UserSessionService } from '../services';
import { IdleService } from '../services/utils/idle.service';
import { TokenService } from '../services/utils/token.service';
import { companyName } from '../utils/constants';

@Directive({
    selector: '[appCatchEvents]',
    standalone: false,
})
export class CatchEventsDirective implements OnDestroy {
    private userActivity?: ReturnType<typeof setTimeout>;
    private endActivity?: ReturnType<typeof setTimeout>;
    private readonly userInactive: Subject<void>;
    private isTokenRefreshed?: ReturnType<typeof setInterval>;
    private subscription: Subscription;

    private lastTokenCheck: number = 0;
    private readonly tokenCheckInterval: number = 60000;

    constructor(
        private readonly idleService: IdleService,
        private readonly tokenService: TokenService,
        private readonly customerService: CustomerService,
        private readonly userSessionService: UserSessionService,
        private readonly cartBillService: CartBillService
    ) {
        this.userInactive = new Subject<void>();
        this.subscription = Subscription.EMPTY;
        this.setIntervalToken();
        this.idleService.initListener();
        this.idleService.setActivity();
        this.listenerUserActivity();
        this.subscribeUserActivity();
    }

    ngOnDestroy(): void {
        this.removeInterval();
        this.subscription.unsubscribe();
    }

    @HostListener('click', ['$event'])
    @HostListener('window:mousemove', ['$event'])
    refreshUserState(): void {
        this.handleUserActivityWithThrottle();
        this.listenerUserActivity();
        this.idleService.setActivity();
    }

    @HostListener('window:beforeunload', ['$event']) beforeUnloadHandler(event: BeforeUnloadEvent) {
        const pageReloaded = window.performance
            .getEntriesByType('navigation')
            .map((nav) => (nav as PerformanceNavigationTiming).type)
            .includes('reload');
        const navigationPage = window.performance
            .getEntriesByType('navigation')
            .map((nav) => (nav as PerformanceNavigationTiming).type)
            .includes('navigate');
        const company: string = this.customerService.getCompanyName();
        if (!pageReloaded && !navigationPage && company === companyName.kn) {
            const resolvedEvent = event || window.event;
            this.logout();
            resolvedEvent.preventDefault();
            resolvedEvent.returnValue = false;
        }
    }

    /**
     * @method listenerUserActivity()
     * @description Update user activity
     */

    private listenerUserActivity(): void {
        clearTimeout(this.userActivity);
        this.userActivity = setTimeout(() => this.userInactive.next(), 300000);
    }

    /**
     * @method setIntervalToken()
     * @description Validate each minute token expiration
     */

    private setIntervalToken(): void {
        this.removeInterval();
        this.isTokenRefreshed = setInterval((): void => {
            this.validateToken();
        }, 60000);
    }

    /**
     * @method subscribeUserActivity()
     * @description Validate token on user activity
     */

    private subscribeUserActivity(): void {
        this.subscription = this.userInactive.subscribe({
            next: (): void => {
                this.validateToken();
            },
            error: (error: Error): void => {
                throwError(() => error);
            },
        });
    }

    /**
     * @method removeInterval()
     * @description Remove the interval
     */

    private removeInterval(): void {
        clearInterval(this.isTokenRefreshed);
    }

    /**
     * @method validateToken()
     * @description Validate one minute before of token expiration
     */

    private validateToken(): void {
        const currentDate = dayjs();
        const token = this.tokenService.getDecodedAccessToken();
        if (token?.exp && !Number.isNaN(Number(token.exp))) {
            const tokenExpDate = dayjs.unix(token.exp);
            if (tokenExpDate.diff(currentDate, 'minute') === 1) {
                if (this.customerHasActivity(currentDate) || this.customerService.getCustomer()?.isGuest) {
                    this.refreshToken();
                } else {
                    const company = this.customerService.getCompanyName();

                    if (company && this.cartBillService.isThereItemsInCart()) {
                        this.showContinueModalWithTimer();
                    } else {
                        this.showContinueModal();
                    }
                }
            }
        } else {
            this.logout();
        }
    }

    /**
     * @method customerHasActivity()
     * @param (currentDate: dayjs.Dayjs)
     * @description Validate user activity
     */

    private customerHasActivity(currentDate: dayjs.Dayjs): boolean {
        return dayjs(currentDate).diff(parseInt(this.idleService.getLastActivity()), 'minute') < 5;
    }

    /**
     * @method logout()
     * @description Clear all the stored data of the user and takes the user to the login page
     */

    private logout(): void {
        Swal.close();
        this.userSessionService.logout();
    }

    /**
     * @method refreshToken()
     * @description Call to refreshToken petition
     */

    private refreshToken(): void {
        this.userSessionService
            .refreshToken()
            .pipe(take(1))
            .subscribe({
                error: (): void => {
                    this.logout();
                },
            });
    }

    /**
     * @method showContinueModalWithTimer()
     * @description Show popup to continue or logout with timer
     */

    private showContinueModalWithTimer(): void {
        this.checkUserTokens();
        this.endActivity = setTimeout(() => {
            this.logout();
        }, 59000);
        let timerInterval: ReturnType<typeof setInterval>;
        let contador = 60;
        Swal.fire({
            title: 'Hey, psst psst!',
            html: `<div> You have been inactive for a long time. Would you like to continue?</div>
      <p style="margin: 10px 0; font-size:24px; font-weight: 500">${contador} s</p>
      <div style="font-size: 14px; margin-top:10px" class="text-muted"> Note: If you decide to log out, the payment requests that are in your cart will be updated to pending status</div>
      `,
            timer: 60000,
            timerProgressBar: false,
            icon: 'info',
            allowOutsideClick: false,
            focusConfirm: false,
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonColor: '#1dd3b0',
            cancelButtonText: 'No, Log out',
            confirmButtonText: 'Yes, continue',
            showCloseButton: false,
            reverseButtons: true,
            allowEscapeKey: false,
            didOpen: () => {
                const b: HTMLElement = Swal.getHtmlContainer()?.querySelector('p') as HTMLElement;
                timerInterval = setInterval(() => {
                    contador--;
                    b.textContent = contador.toString() + ' s';
                }, 1000);
            },
            willClose: () => {
                clearInterval(timerInterval);
            },
        }).then((result): void => {
            clearTimeout(this.endActivity);
            if (result.isConfirmed) {
                this.refreshToken();
            } else if (result.dismiss) {
                this.logout();
            }
        });
    }

    /**
     * @method showContinueModal()
     * @description Show popup to continue or logout
     */

    private showContinueModal(): void {
        this.checkUserTokens();
        this.endActivity = setTimeout(() => {
            this.logout();
        }, 59000);

        Swal.fire({
            html: `
            <div style="display: flex; flex-direction: column; align-items: center; text-align: center;">
                <div class="svg-container">
                    <svg xmlns="http://www.w3.org/2000/svg" width="92" height="92"  viewBox="0 0 93 92" fill="none">
                    <circle cx="46.5" cy="46" r="36" stroke="#D1A245" stroke-width="4"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" 
                    d="M43.2275 28H49.773L48.9548 53.3636H44.0457L43.2275 28ZM49.7729 60.7273C49.7729 62.5347 48.3076 64 46.5001 64C44.6927 64 43.2274 62.5347 43.2274 60.7273C43.2274 58.9198 44.6927 57.4545 46.5001 57.4545C48.3076 57.4545 49.7729 58.9198 49.7729 60.7273Z"
                    fill="#D1A245"/>
                    </svg>
                </div>
                <h2 style="font-weight: bold; margin-top: 10px; font-color:#212121">Session Timeout Alert!</h2>
                <p style="max-width: 417px; font-color:#212121"">You've been inactive for a while. For your security, please choose an option.</p>
            </div>
                `,
            allowOutsideClick: false,
            focusConfirm: false,
            showCancelButton: true,
            cancelButtonText: 'LOG OUT',
            confirmButtonText: 'CONTINUE',
            showCloseButton: false,
            reverseButtons: true,
            allowEscapeKey: false,
            customClass: {
                cancelButton: 'cancel-sweet',
                confirmButton: 'confirm-sweet',
            },
        }).then((result): void => {
            clearTimeout(this.endActivity);
            if (result.isConfirmed) {
                this.refreshToken();
            } else if (result.dismiss) {
                this.logout();
            }
        });
    }

    private handleUserActivityWithThrottle(): void {
        const now = Date.now();
        if (now - this.lastTokenCheck > this.tokenCheckInterval) {
            this.lastTokenCheck = now;
            this.checkUserTokens();
        }
    }

    private checkUserTokens(): void {
        const accessToken = this.tokenService.getCurrentUser();
        const refreshToken = this.tokenService.getCurrentAccessControl();

        if (!accessToken || !refreshToken) {
            return;
        }

        if (this.tokenService.isTokenExpired(accessToken) || this.tokenService.isTokenExpired(refreshToken)) {
            this.logout();
        }
    }
}
